import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';

interface Testimonial {
  id: number;
  name: string;
  title: string;
  company: string;
  content: string;
  image?: string;
}

const Testimonials: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [activeHighlightIndex, setActiveHighlightIndex] = useState(-1);
  const [currentHighlights, setCurrentHighlights] = useState<Array<{phrase: string, start: number, end: number}>>([]);

  // Touch swipe support
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);
  const minSwipeDistance = 50;

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = e.touches[0].clientX;
    setIsAutoPlaying(false);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(() => {
    const distance = touchStartX.current - touchEndX.current;
    if (Math.abs(distance) >= minSwipeDistance) {
      if (distance > 0) {
        goToNext();
      } else {
        goToPrevious();
      }
    }
    setIsAutoPlaying(true);
  }, []);

  // Keywords/phrases to highlight
  // Quotes run verbatim and static; no phrase highlighting.
  const highlightPhrases: string[] = [];

  // Function to find all highlights in text
  const findHighlights = (text: string) => {
    const highlights: Array<{phrase: string, start: number, end: number}> = [];
    
    highlightPhrases.forEach(phrase => {
      const regex = new RegExp(phrase, 'gi');
      let match;
      while ((match = regex.exec(text)) !== null) {
        highlights.push({
          phrase: match[0],
          start: match.index,
          end: match.index + match[0].length
        });
      }
    });
    
    // Sort by position
    highlights.sort((a, b) => a.start - b.start);
    return highlights;
  };

  // Function to parse content and add highlights
  const parseContent = (text: string) => {
    if (currentHighlights.length === 0) return text;
    
    // Build JSX array
    const elements: React.ReactNode[] = [];
    let lastEnd = 0;
    
    currentHighlights.forEach((highlight, index) => {
      // Add text before highlight
      if (highlight.start > lastEnd) {
        elements.push(text.substring(lastEnd, highlight.start));
      }
      
      // Add highlighted text
      elements.push(
        <span
          key={index}
          className={`highlight-phrase ${index === activeHighlightIndex ? 'active' : ''}`}
        >
          {highlight.phrase}
        </span>
      );
      
      lastEnd = highlight.end;
    });
    
    // Add remaining text
    if (lastEnd < text.length) {
      elements.push(text.substring(lastEnd));
    }
    
    return elements;
  };

  const testimonials: Testimonial[] = [
    {
      id: 1,
      name: "Christophe Galland",
      title: "Founder & CEO",
      company: "The Imagists",
      content: "Ben has been one of our most trusted developers for many years. I can commend his dedication, talent for coding, and - frankly - excellent ability to find solutions and make things happen. He is also a great person, with a consistently awesome attitude. His ability to turn our designs into pixel-perfect sites is unparalleled, and he would be a rare and excellent addition to any team!",
      image: "/testimonials/christophe.jpeg"
    },
    {
      id: 2,
      name: "Amanda Moret",
      title: "Founder",
      company: "Installation Magazine",
      content: "Ben Smith is a truly exceptional individual whose extensive knowledge of WordPress has made a tremendous impact on my business. I began working with Ben nearly ten years ago and I couldn't imagine having a better web developer to have on my team. He is sharp, intelligent, kind and a true asset. The strength of his character is what makes Ben so special. His web development acumen is stellar but his transparency is what sets him a part. Ben is trustworthy, pragmatic and is always seeking ways to refine his skill set.",
      image: "/testimonials/amanda.jpeg"
    },
    {
      id: 3,
      name: "Josh Woolf",
      title: "Creative Producer",
      company: "VHF // VERY HIGH FREQUENCY",
      content: "Our agency has had the luxury of working with Ben for over 8 years. He is a phenomenal developer and highly skilled in WordPress. I've personally been impressed by his ability to self-motivate and grow his skillset and knowledge as the industry rapidly changes. I truly trust Ben. He is always fair and honest in his work.",
      image: "/testimonials/josh.jpeg"
    },
    {
      id: 4,
      name: "David Cosgrove",
      title: "Owner",
      company: "David Cosgrove Los Angeles Web Design",
      content: "I have been in the business for 25+ years, and Ben Smith is one of the most valuable people I have ever worked with. Wise, smart, excellent, and a well-educated Web Developer who can be trusted.",
      image: "/testimonials/david-cosgrove.jpeg"
    }
  ];

  // Update highlights when testimonial changes
  useEffect(() => {
    const newHighlights = findHighlights(testimonials[currentIndex].content);
    setCurrentHighlights(newHighlights);
    setActiveHighlightIndex(-1);
    
    // Start highlighting after a brief delay
    setTimeout(() => {
      setActiveHighlightIndex(0);
    }, 1000);
  }, [currentIndex]);

  // Cycle through highlights one at a time
  useEffect(() => {
    if (currentHighlights.length === 0 || activeHighlightIndex === -1) return;
    
    const timeout = setTimeout(() => {
      if (activeHighlightIndex >= currentHighlights.length - 1) {
        // All highlights done, advance to next slide after a short delay
        setTimeout(() => {
          if (isAutoPlaying) {
            setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
          }
        }, 2000); // Wait 2 seconds after last highlight before advancing
      } else {
        // Move to next highlight
        setActiveHighlightIndex(prev => prev + 1);
      }
    }, 2500); // 2.5 seconds for each highlight

    return () => clearTimeout(timeout);
  }, [currentHighlights.length, isAutoPlaying, activeHighlightIndex]);

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  return (
    <div className="py-24 px-6 bg-neutral-50 dark:bg-neutral-900">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold mb-12 md:mb-16 text-center text-primary-700 dark:text-gray-100">
          From clients
        </h2>
        
        <div 
          className="relative"
          onMouseEnter={() => setIsAutoPlaying(false)}
          onMouseLeave={() => setIsAutoPlaying(true)}
        >
          <div
            className="bg-white dark:bg-neutral-800 rounded-2xl shadow-lg p-8 md:p-12 relative overflow-hidden touch-pan-y"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <Quote className="absolute top-4 left-4 text-primary-200/30 dark:text-primary-900/20 w-16 h-16 md:w-20 md:h-20" />
            
            <div className="relative z-10">
              <div className="mb-8">
                <p className="text-lg md:text-xl text-gray-700 dark:text-gray-300 leading-relaxed italic">
                  "{parseContent(testimonials[currentIndex].content)}"
                </p>
              </div>
              
              <div className="flex items-center gap-4">
                {testimonials[currentIndex].image && (
                  <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700 flex-shrink-0">
                    <img 
                      src={testimonials[currentIndex].image} 
                      alt={testimonials[currentIndex].name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div>
                  <h3 className="font-bold text-lg text-gray-900 dark:text-gray-100">
                    {testimonials[currentIndex].name}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {testimonials[currentIndex].title}, {testimonials[currentIndex].company}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={goToPrevious}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 bg-white dark:bg-neutral-800 rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label="Previous testimonial"
          >
            <ChevronLeft className="w-5 h-5 text-gray-700 dark:text-gray-300" />
          </button>
          
          <button
            onClick={goToNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 bg-white dark:bg-neutral-800 rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label="Next testimonial"
          >
            <ChevronRight className="w-5 h-5 text-gray-700 dark:text-gray-300" />
          </button>

          {/* Pagination Dots */}
          <div className="flex justify-center gap-2 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                  index === currentIndex
                    ? 'bg-primary-600 dark:bg-primary-400 w-8'
                    : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Testimonials;